import React, { useState, useEffect, useCallback } from 'react';
import { collection } from 'firebase/firestore';
import { db } from '../firebase';
import JitsiJoinModal from './JitsiJoinModal';
import { getJitsiRoomName, getPreferredJoinURL } from '../utils/jitsiHelper';
import './AdminOnlineClassesView.css';
import './AdminMoreDetailPages.css';
import { scopeBySchool } from '../utils/schoolScope';
import { dataCacheService } from '../services/DataCacheService';

function AdminOnlineClassesView({ schoolId }) {
  const [onlineClasses, setOnlineClasses] = useState([]);
  const [classMap, setClassMap] = useState({});
  const [selectedClass, setSelectedClass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [jitsiModal, setJitsiModal] = useState({ isOpen: false, roomName: '', displayName: '' });

  const loadOnlineClasses = useCallback(async () => {
    try {
      setLoading(true);
      setOnlineClasses([]);

      // Fetch all classes for the school (cached, school-scoped key)
      const ck = (p) => `${String(schoolId || 'all').toLowerCase()}:${p}`;
      const scopedClasses = scopeBySchool(
        await dataCacheService.getDocs(ck('classes'), collection(db, 'classes')),
        schoolId
      );
      const classIds = scopedClasses.map(d => d.id);
      const cMap = {};
      scopedClasses.forEach(c => { cMap[c.id] = c; });
      setClassMap(cMap);

      if (classIds.length === 0) {
        setLoading(false);
        return;
      }

      // Fetch online classes for each class in the school (parallel, cached)
      const perClass = await Promise.all(classIds.map(classId =>
        dataCacheService.getDocs(ck(`classes/${classId}/onlineClasses`), collection(db, 'classes', classId, 'onlineClasses'))
      ));
      const allOnlineClasses = [];
      classIds.forEach((classId, i) => {
        allOnlineClasses.push(...perClass[i].map(d => ({ ...d, classId })));
      });

      // Sort by date (newest first)
      allOnlineClasses.sort((a, b) => new Date(b.date) - new Date(a.date));
      setOnlineClasses(allOnlineClasses);
    } catch (error) {
      console.error('Error loading online classes:', error);
    } finally {
      setLoading(false);
    }
  }, [schoolId]);

  useEffect(() => {
    loadOnlineClasses();
  }, [loadOnlineClasses]);

  const handleJoinClass = (onlineClass) => {
    const joinURL = getPreferredJoinURL(onlineClass);
    if (!joinURL) {
      alert('This class is not joinable at this time.');
      return;
    }

    if (!joinURL.includes('meet.jit.si')) {
      window.open(joinURL, '_blank');
      return;
    }

    const roomName = getJitsiRoomName(onlineClass.id);
    const displayName = 'Admin (Observer)';
    setJitsiModal({ isOpen: true, roomName, displayName });
  };

  const closeJitsiModal = () => {
    setJitsiModal({ isOpen: false, roomName: '', displayName: '' });
  };

  const canJoin = (onlineClass) => {
    const isOnline = onlineClass.classMode === 'Online' || onlineClass.classMode === 'Hybrid';
    const isActive = onlineClass.status === 'Active' || onlineClass.status === 'active';
    const now = new Date();
    const classDate = new Date(onlineClass.date);
    const isToday = classDate.toDateString() === now.toDateString();
    return isOnline && (isActive || isToday) && getPreferredJoinURL(onlineClass);
  };

  const filteredClasses = onlineClasses.filter(c => {
    if (statusFilter === 'all') return true;
    return c.status?.toLowerCase() === statusFilter.toLowerCase();
  });

  const activeCount = onlineClasses.filter(c => c.status === 'Active' || c.status === 'active').length;
  const scheduledCount = onlineClasses.filter(c => c.status === 'Scheduled' || c.status === 'scheduled').length;
  const completedCount = onlineClasses.filter(c => c.status === 'Completed' || c.status === 'completed').length;

  // === Full-screen online class detail page ===
  if (selectedClass) {
    const oc = selectedClass;
    const joinURL = getPreferredJoinURL(oc);
    const roomName = getJitsiRoomName(oc.id);
    const cls = classMap[oc.classId];
    const classLabel = oc.className || (cls ? `${cls.name}${cls.grade ? ` (Grade ${cls.grade})` : ''}` : oc.classId);
    const statusLower = (oc.status || '').toLowerCase();
    console.log('🔥 AdminOnlineClasses: Showing detail for class', oc.id, oc);
    return (
      <div className="admin-online-classes-view detail-page">
        <button className="detail-back-button" onClick={() => setSelectedClass(null)}>
          ◄ Back to Online Classes
        </button>
        <div className="detail-card">
          <div className="detail-header">
            <div>
              <h2>{oc.classMode === 'Online' ? '📹' : oc.classMode === 'Hybrid' ? '🔄' : '🏫'} {oc.subjectName || 'Online Class'}</h2>
              <p className="detail-subtitle">{oc.topic || 'No topic'}</p>
            </div>
            <span className="detail-status-badge" style={{
              background: statusLower === 'active' ? '#d1fae5' : statusLower === 'scheduled' ? '#dcfce7' : '#f3f4f6',
              color: statusLower === 'active' ? '#065f46' : statusLower === 'scheduled' ? '#15803d' : '#374151'
            }}>
              {oc.status || 'unknown'}
            </span>
          </div>

          <div className="detail-grid">
            <div className="detail-field">
              <span className="detail-label">Class</span>
              <span className="detail-value">{classLabel || 'N/A'}</span>
            </div>
            <div className="detail-field">
              <span className="detail-label">Subject</span>
              <span className="detail-value">{oc.subjectName || 'N/A'}</span>
            </div>
            <div className="detail-field">
              <span className="detail-label">Teacher</span>
              <span className="detail-value">👨‍🏫 {oc.teacherName || 'N/A'}</span>
            </div>
            <div className="detail-field">
              <span className="detail-label">Date</span>
              <span className="detail-value">{oc.date ? new Date(oc.date).toLocaleDateString() : 'N/A'}</span>
            </div>
            <div className="detail-field">
              <span className="detail-label">Time</span>
              <span className="detail-value">⏰ {oc.startTime || '—'} – {oc.endTime || '—'}</span>
            </div>
            <div className="detail-field">
              <span className="detail-label">Mode</span>
              <span className="detail-value">{oc.classMode || 'N/A'}</span>
            </div>
            <div className="detail-field">
              <span className="detail-label">Status</span>
              <span className="detail-value">{oc.status || 'N/A'}</span>
            </div>
            {roomName && (
              <div className="detail-field">
                <span className="detail-label">Jitsi Room</span>
                <span className="detail-value">{roomName}</span>
              </div>
            )}
            {joinURL && (
              <div className="detail-field">
                <span className="detail-label">Join Link</span>
                <span className="detail-value">
                  <a href={joinURL} target="_blank" rel="noopener noreferrer" style={{ color: '#16a34a' }}>
                    {joinURL}
                  </a>
                </span>
              </div>
            )}
          </div>

          {(oc.description || oc.notes) && (
            <div className="detail-section">
              <h4>Description</h4>
              <p className="detail-text">{oc.description || oc.notes}</p>
            </div>
          )}

          <div className="detail-actions">
            {canJoin(oc) && (
              <button className="detail-btn-success" onClick={() => handleJoinClass(oc)}>
                📹 Join (Observe)
              </button>
            )}
            {joinURL && !canJoin(oc) && (
              <a className="detail-btn-primary" href={joinURL} target="_blank" rel="noopener noreferrer"
                style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', padding: '10px 20px', borderRadius: '8px', fontWeight: 600 }}>
                Open Join Link
              </a>
            )}
          </div>
        </div>

        <JitsiJoinModal
          isOpen={jitsiModal.isOpen}
          roomName={jitsiModal.roomName}
          displayName={jitsiModal.displayName}
          onClose={closeJitsiModal}
        />
      </div>
    );
  }

  return (
    <div className="admin-online-classes-view">
      <h2>Online Classes (School-Wide)</h2>

      {loading ? (
        <div className="loading">Loading online classes...</div>
      ) : onlineClasses.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📹</div>
          <h3>No Online Classes</h3>
          <p>Online classes scheduled by teachers across the school will appear here.</p>
        </div>
      ) : (
        <div className="online-classes-container">
          <div className="summary-cards">
            <div className="summary-card active">
              <div className="summary-icon">🟢</div>
              <div className="summary-value">{activeCount}</div>
              <div className="summary-label">Active</div>
            </div>
            <div className="summary-card scheduled">
              <div className="summary-icon">📅</div>
              <div className="summary-value">{scheduledCount}</div>
              <div className="summary-label">Scheduled</div>
            </div>
            <div className="summary-card completed">
              <div className="summary-icon">✅</div>
              <div className="summary-value">{completedCount}</div>
              <div className="summary-label">Completed</div>
            </div>
          </div>

          <div className="filter-bar">
            <label>Filter by status:</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All</option>
              <option value="scheduled">Scheduled</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div className="classes-list">
            {filteredClasses.map(onlineClass => (
              <div
                key={onlineClass.id}
                className="class-card"
                style={{ cursor: 'pointer' }}
                onClick={() => setSelectedClass(onlineClass)}
              >
                <div className="class-header">
                  <span className={`class-mode ${onlineClass.classMode?.toLowerCase()}`}>
                    {onlineClass.classMode === 'Online' ? '📹' : onlineClass.classMode === 'Hybrid' ? '🔄' : '🏫'}
                  </span>
                  <span className={`class-status ${onlineClass.status?.toLowerCase()}`}>
                    {onlineClass.status}
                  </span>
                </div>
                <h3>{onlineClass.subjectName || 'Class'}</h3>
                <p className="topic">{onlineClass.topic || 'No topic'}</p>
                <div className="class-meta">
                  <span>👨‍🏫 {onlineClass.teacherName || 'Teacher'}</span>
                  <span>📅 {new Date(onlineClass.date).toLocaleDateString()}</span>
                  <span>⏰ {onlineClass.startTime} - {onlineClass.endTime}</span>
                </div>
                {canJoin(onlineClass) && (
                  <button className="join-button" onClick={(e) => { e.stopPropagation(); handleJoinClass(onlineClass); }}>
                    Join (Observe)
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <JitsiJoinModal
        isOpen={jitsiModal.isOpen}
        roomName={jitsiModal.roomName}
        displayName={jitsiModal.displayName}
        onClose={closeJitsiModal}
      />
    </div>
  );
}

export default AdminOnlineClassesView;
